import React from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';

import Text from '../global/Text';
import fontMaker from '../../utils/font';
import { colors } from '../../constants/theme';
import type { ScheduleListItemData, ScheduleItemStatus, ScheduleItemType } from '../../typing/scheduleItem';

interface ScheduleListItemProps {
  item: ScheduleListItemData;
  onPress: (item: ScheduleListItemData) => void;
}

const statusColorMap: Record<ScheduleItemStatus, string> = {
  'not-started': colors.gray,
  'in-progress': colors.secondary,
  completed: colors.primary,
  overdue: colors.semanticError
};

const typeColorMap: Record<ScheduleItemType, string> = {
  scheduled: colors.primary,
  adhoc: colors.nimblyPurple
};

function formatScheduleTime(item: ScheduleListItemData): string {
  if (item.isStartAnytime) {
    return '';
  }

  const tz = item.siteTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const startMoment = moment.tz(item.scheduledDate, tz).startOf('day').add(item.startTime, 'minutes');
  let endMoment = moment.tz(item.scheduledDate, tz).startOf('day').add(item.endTime, 'minutes');

  // midnight-crossing: if end is before start, the schedule spans into the next day
  if (item.endTime < item.startTime) {
    endMoment = endMoment.add(1, 'day');
  }

  const offset = startMoment.format('Z');
  const dateFmt = moment(item.scheduledDate).format('D MMM YYYY');
  const startFmt = startMoment.locale('en').format('hh:mm A');
  const endFmt = endMoment.locale('en').format('hh:mm A');

  if (startMoment.isSame(endMoment, 'day')) {
    return `${dateFmt} ${startFmt} - ${endFmt} GMT${offset}`;
  }
  return `${dateFmt} ${startFmt} - ${endMoment.format('D MMM YYYY')} ${endFmt} GMT${offset}`;
}

const ScheduleListItem: React.FC<ScheduleListItemProps> = ({ item, onPress }) => {
  const { t } = useTranslation('home');

  const statusColor = item.isPendingUpload ? colors.primary : statusColorMap[item.status];
  const statusLabel = item.isPendingUpload
    ? t('newHome.pendingUpload')
    : item.status === 'not-started'
    ? t('newHome.notStarted')
    : item.status === 'in-progress'
    ? t('newHome.inProgress')
    : item.status === 'completed'
    ? t('newHome.completed')
    : t('newHome.overdue');

  const timeText = item.isStartAnytime ? t('newHome.startAnytime') : formatScheduleTime(item);
  const completionText = `${item.totalAnswered}/${item.totalQuestion} ${t('newHome.completed')}`;
  const typeBadgeColor = typeColorMap[item.scheduleType];
  const typeLabel = item.scheduleType === 'scheduled' ? t('newHome.scheduled') : t('newHome.adhoc');

  return (
    <TouchableOpacity activeOpacity={0.75} onPress={() => onPress(item)} style={styles.card}>
      {/* Row 1: Questionnaire name + Status */}
      <View style={styles.row}>
        <Text style={styles.questionnaireName} numberOfLines={2}>
          {item.questionnaireName}
        </Text>
        <Text style={[styles.statusText, { color: statusColor }]}>{statusLabel}</Text>
      </View>

      {/* Row 2: Date/time */}
      <View style={styles.infoRow}>
        <Text style={styles.infoText}>{timeText}</Text>
      </View>

      {/* Row 3: Completion count */}
      <View style={styles.infoRow}>
        <Text style={styles.infoText}>{completionText}</Text>
      </View>

      {/* Row 4: Location + Type badge */}
      <View style={styles.bottomRow}>
        <View style={styles.locationRow}>
          <Icon name="map-marker" size={16} color={colors.gray} />
          <Text style={styles.locationText} numberOfLines={1}>
            {item.siteName}
          </Text>
        </View>
        <View style={[styles.typeBadge, { backgroundColor: typeBadgeColor }]}>
          <Text style={styles.typeBadgeText}>{typeLabel}</Text>
        </View>
      </View>
    </TouchableOpacity>
  );
};

export default React.memo(ScheduleListItem);

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#fff',
    marginHorizontal: 16,
    marginBottom: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E3E8EF',
    paddingHorizontal: 16,
    paddingVertical: 14,
    elevation: 7,
    shadowColor: '#0F172A',
    shadowOpacity: 0.14,
    shadowOffset: { width: 0, height: 6 },
    shadowRadius: 12
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 6
  },
  questionnaireName: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 16,
    lineHeight: 22,
    flex: 1,
    marginRight: 8,
    color: '#25282B'
  },
  statusText: {
    ...fontMaker({ weight: 'Normal' }),
    fontSize: 13,
    lineHeight: 22
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4
  },
  infoText: {
    ...fontMaker({ weight: 'Light' }),
    fontSize: 13,
    color: colors.darkgray,
    lineHeight: 18
  },
  bottomRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 4
  },
  locationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1
  },
  locationText: {
    ...fontMaker({ weight: 'Light' }),
    fontSize: 13,
    color: colors.darkgray,
    marginLeft: 4
  },
  typeBadge: {
    borderRadius: 14,
    paddingHorizontal: 12,
    paddingVertical: 4
  },
  typeBadgeText: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 12,
    color: '#fff'
  }
});
